import { useEffect, useState, useRef } from 'react';
import { api } from '../api/client';
import { Stack, Row, Text, Avatar, Button } from './index';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [unread, setUnread] = useState(0);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  async function refresh() {
    try {
      const r = await api.notifications();
      setItems(r.notifications); setUnread(r.unread);
    } catch {}
  }

  useEffect(() => {
    refresh();
    const i = setInterval(refresh, 30_000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  async function markRead(id: string) {
    setItems(items.map(x => x.id === id ? { ...x, readAt: Date.now() } : x));
    setUnread(Math.max(0, unread - 1));
    try { await api.markNotificationRead(id); } catch {}
  }
  async function markAll() {
    setBusy(true);
    try { await api.markAllRead(); await refresh(); } finally { setBusy(false); }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="relative h-8 w-8 inline-flex items-center justify-center hairline rounded-sm hover:bg-whisper transition-colors"
        title="notifications"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M6 8a6 6 0 0112 0c0 7 3 9 3 9H3s3-2 3-9z" />
          <path d="M10 21a2 2 0 004 0" />
        </svg>
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 rounded-full bg-negative text-bg text-[9px] font-bold flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-9 w-screen sm:w-[380px] max-w-[100vw] sm:max-w-[90vw] bg-bg hairline rounded-sm z-50 shadow-lg">
          <div className="hairline-b px-3 h-9 flex items-center justify-between">
            <Text variant="micro">notifications</Text>
            {unread > 0 && <button onClick={markAll} disabled={busy} className="text-xs text-muted hover:text-ink">mark all read</button>}
          </div>
          <div className="max-h-[480px] overflow-auto">
            {items.length === 0 ? (
              <div className="px-3 py-6 text-center"><Text variant="label">no notifications</Text></div>
            ) : items.map(n => (
              <button
                key={n.id}
                onClick={() => markRead(n.id)}
                className={clsx('w-full text-left px-3 py-2 hairline-b last:border-b-0 hover:bg-whisper transition-colors',
                  !n.readAt && 'bg-accent/5')}>
                <Stack gap={0.5}>
                  <Row gap={1}>
                    {!n.readAt && <span className={`mt-1 inline-block h-1.5 w-1.5 rounded-full shrink-0 ${n.kind === 'blacklist_alert' ? 'bg-negative' : (n.kind === 'direct_message' || n.kind === 'voice_note') ? 'bg-blue-500' : 'bg-accent'}`} />}
                    <Stack gap={0.5} className="flex-1">
                      <Text variant="small" className={`font-medium ${n.kind === 'blacklist_alert' ? 'text-negative' : ''}`}>{n.title}</Text>
                      {n.body && <Text variant="small" muted>{n.body}</Text>}
                      <Text variant="label">{new Date(n.createdAt).toLocaleString()} · {n.kind}</Text>
                    </Stack>
                  </Row>
                  {n.kind === 'overlap' && (
                    <Row gap={1} className="mt-1">
                      <Link to="/hangs" className="text-xs text-accent hover:underline">plan a hang →</Link>
                    </Row>
                  )}
                  {n.kind === 'blacklist_alert' && n.data?.dealId && (
                    <Row gap={1} className="mt-1">
                      <Link to={`/deals/${n.data.dealId}`} className="text-xs text-negative hover:underline" onClick={() => markRead(n.id)}>view deal →</Link>
                    </Row>
                  )}
                  {(n.kind === 'direct_message' || n.kind === 'voice_note') && n.data?.fromFriendId && (
                    <Row gap={1} className="mt-1">
                      <Link to="/people" className="text-xs text-accent hover:underline" onClick={() => markRead(n.id)}>
                        {n.kind === 'voice_note' ? 'play voice note →' : 'open conversation →'}
                      </Link>
                    </Row>
                  )}
                </Stack>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
